import { useEffect, useState } from 'react';
import {
  Timestamp,
  collection,
  doc,
  getDoc,
  onSnapshot,
  query,
  updateDoc,
  where,
} from 'firebase/firestore';
import { db } from '@/core/firebase';
import type { Client, WorkoutAssignment, WorkoutSession } from '../types';

export function useClientDetail(trainerId?: string, clientId?: string) {
  const [client, setClient] = useState<Client | null>(null);
  const [assignments, setAssignments] = useState<WorkoutAssignment[]>([]);
  const [sessions, setSessions] = useState<WorkoutSession[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!clientId) return;
    getDoc(doc(db, 'clients', clientId))
      .then((snap) => {
        if (snap.exists()) setClient({ id: snap.id, ...snap.data() } as Client);
      })
      .catch(() => setError('Unable to load client'));
  }, [clientId]);

  useEffect(() => {
    const linkedUserId = client?.status === 'linked' ? client.userId : undefined;
    if (!trainerId || !linkedUserId) {
      setAssignments([]);
      setSessions([]);
      return;
    }

    const assignmentQuery = query(
      collection(db, 'workoutAssignments'),
      where('trainerId', '==', trainerId),
      where('clientId', '==', linkedUserId)
    );
    const sessionQuery = query(
      collection(db, 'workoutSessions'),
      where('trainerId', '==', trainerId),
      where('clientId', '==', linkedUserId)
    );

    const unsubAssignments = onSnapshot(assignmentQuery, (snap) => {
      const docs = snap.docs.map(
        (docSnap) => ({ id: docSnap.id, ...docSnap.data() } as WorkoutAssignment)
      );
      setAssignments(docs);
    });

    const unsubSessions = onSnapshot(sessionQuery, (snap) => {
      const docs = snap.docs.map(
        (docSnap) => ({ id: docSnap.id, ...docSnap.data() } as WorkoutSession)
      );
      setSessions(
        docs.sort(
          (a, b) =>
            ((b.startedAt as Timestamp)?.toMillis?.() || 0) -
            ((a.startedAt as Timestamp)?.toMillis?.() || 0)
        )
      );
    });

    return () => {
      unsubAssignments();
      unsubSessions();
    };
  }, [trainerId, client]);

  const saveNotes = async (notes: string) => {
    if (!clientId) return;
    await updateDoc(doc(db, 'clients', clientId), {
      trainerNotes: notes,
      updatedAt: Timestamp.now(),
    });
  };

  return { client, assignments, sessions, saveNotes, error };
}
